import ReceiptRulesUpsellSvg from 'ext-ui/svg/illustration/ReceiptRulesUpsellSvg'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import OrgSettingsReceiptRulesBox from 'src/components/OrgSettingsReceiptRulesBox/OrgSettingsReceiptRulesBox'
import SaasUpgradeOrContent from 'src/feature/SaasUpgradeOrContent/SaasUpgradeOrContent'
import { useBackgroundDispatch } from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import {
  getOrganization,
  getOrganizationCurrencies,
} from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectExpenseRequirementsWhitelistFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const ReceiptRules: React.FC = () => {
  const translate = useTranslate()
  const user = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const appName = useSelector(selectAppName)

  const dispatch = useBackgroundDispatch()

  useEffect(() => {
    dispatch(getOrganization(organizationId, true))
  }, [])

  useEffect(() => {
    if (organization?.currencies === undefined) {
      dispatch(getOrganizationCurrencies(organizationId))
    }
  }, [organization?.currencies])

  const showNewOrgSettings = true
  const Container = showNewOrgSettings ? 'div' : BackgroundPageContent

  const extendLocation = useExtendLocation()
  const { show_expense_requirements } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const expReqsWhitelist = useSelector(
    selectExpenseRequirementsWhitelistFromRemoteConfigState,
  )
  const showExpenseRequirements =
    show_expense_requirements || expReqsWhitelist?.includes(organizationId)

  if (
    user === undefined ||
    !user.hasOrgBookkeeperPermissions ||
    !organization ||
    // Show 404 if user navigates to Receipt Rules when showExpenseRequirements is on
    // since Receipt Rules is moved to Expense Requirements
    (showExpenseRequirements &&
      extendLocation.location ===
        AppLocation.OrganizationSettingsReceiptRules) ||
    // similarly, show 404 if user navigates to Expense Requirements Receipt Rules when showExpenseRequirements is off
    (!showExpenseRequirements &&
      extendLocation.location ===
        AppLocation.OrganizationSettingsExpenseRequirementsReceiptRules)
  ) {
    return <NotFoundBackgroundPageContent />
  }

  return (
    <>
      <Helmet>
        <title>{`Receipt Rules | ${appName}`}</title>
        <meta name="description" content={`Receipt Rules page of ${appName}`} />
      </Helmet>
      <SaasUpgradeOrContent
        illustration={<ReceiptRulesUpsellSvg />}
        title={translate('Receipt Rules')}
        description={translate(
          'Create receipt rules for expenses that require documentation and send reminders to people who are missing their receipts.',
        )}
        ctaDetailSrc={
          'Upgrade to ${tier} today and improve receipt compliance across your organization.'
        }
        feature={UserFeature.BUSINESS_RULES}
      >
        <Container>
          <div
            css={css`
              max-width: 570px;
            `}
          >
            <OrgSettingsReceiptRulesBox
              organization={organization}
              user={user}
            />
          </div>
        </Container>
      </SaasUpgradeOrContent>
    </>
  )
}

export default ReceiptRules
